import { useEffect } from "react";
import type { AuthCompany } from "./use-auth";

// Converts a hex color (#rrggbb or #rgb) into an "H S% L%" triplet
// compatible with the app's `hsl(...)` CSS custom properties.
function hexToHslTriplet(hex: string): string | null {
  const clean = hex.trim().replace(/^#/, "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }

  return `${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`;
}

// Picks a readable foreground (near-black or white) for a given HSL lightness.
function foregroundFor(hslTriplet: string): string {
  const parts = hslTriplet.split(" ");
  const lightness = parseFloat(parts[2] || "50");
  return lightness > 60 ? "0 0% 10%" : "0 0% 100%";
}

const OVERRIDE_VARS = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-ring",
  "--chart-1",
];

const ACCENT_OVERRIDE_VARS = ["--accent", "--accent-foreground", "--chart-2", "--sidebar-accent-foreground"];

/**
 * Applies a company's white-label branding (primaryColor / accentColor) to the
 * document root as CSS custom-property overrides. Companies without custom
 * colors simply fall back to the app's default real-estate theme (index.css).
 * Call with `undefined`/`null` to clear overrides (e.g. on logout).
 */
export function useCompanyTheme(company?: AuthCompany | null) {
  useEffect(() => {
    const root = document.documentElement;

    if (company?.primaryColor) {
      const hsl = hexToHslTriplet(company.primaryColor);
      if (hsl) {
        const fg = foregroundFor(hsl);
        root.style.setProperty("--primary", `hsl(${hsl})`);
        root.style.setProperty("--primary-foreground", `hsl(${fg})`);
        root.style.setProperty("--ring", `hsl(${hsl})`);
        root.style.setProperty("--sidebar-primary", `hsl(${hsl})`);
        root.style.setProperty("--sidebar-primary-foreground", `hsl(${fg})`);
        root.style.setProperty("--sidebar-ring", `hsl(${hsl})`);
        root.style.setProperty("--chart-1", `hsl(${hsl})`);
      }
    } else {
      OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
    }

    if (company?.accentColor) {
      const hsl = hexToHslTriplet(company.accentColor);
      if (hsl) {
        const fg = foregroundFor(hsl);
        root.style.setProperty("--accent", `hsl(${hsl})`);
        root.style.setProperty("--accent-foreground", `hsl(${fg})`);
        root.style.setProperty("--chart-2", `hsl(${hsl})`);
        root.style.setProperty("--sidebar-accent-foreground", `hsl(${hsl})`);
      }
    } else {
      ACCENT_OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
    }

    if (company?.name) {
      document.title = `${company.name} — ${document.title.split("—").pop()?.trim() || "Rently"}`;
    }

    return () => {
      OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
      ACCENT_OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
    };
  }, [company?.primaryColor, company?.accentColor, company?.name]);
}
